<script setup lang="ts">
import { Btn, Dropdown, TextInput } from '@bagelink/vue'
import { nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const props = withDefaults(
	defineProps<{
		placeholder?: string
		searchPath?: string
	}>(),
	{
		placeholder: 'Search...',
		searchPath: '/search',
	},
)

const router = useRouter()
const route = useRoute()
const query = ref('')
const searchInput = ref<InstanceType<typeof TextInput>>()

// Keep the header field in sync with the URL (back/forward, or landing on
// `/search?q=...` from a shared link) so it never shows a stale query.
watch(
	() => route.query.q,
	(value) => {
		if (route.path !== props.searchPath) return
		const next = typeof value === 'string' ? value : ''
		if (next !== query.value) query.value = next
	},
	{ immediate: true },
)

const keyboardRows = [
	['ק', 'ר', 'א', 'ט', 'ו', 'ן', 'ם', 'פ'],
	['ש', 'ד', 'ג', 'כ', 'ע', 'י', 'ח', 'ל', 'ך', 'ף'],
	['ז', 'ס', 'ב', 'ה', 'נ', 'מ', 'צ', 'ת', 'ץ'],
]

async function focusInput() {
	await nextTick()
	searchInput.value?.focus()
}

function addCharacter(character: string) {
	query.value += character
	focusInput()
}

function removeCharacter() {
	query.value = query.value.slice(0, -1)
	focusInput()
}

function clearSearch() {
	query.value = ''
	focusInput()
}

function runSearch() {
	const value = query.value.trim()
	if (value) router.push({ path: props.searchPath, query: { q: value } })
}
</script>

<template>
	<form class="hebrew-search flex align-items-center" role="search" @submit.prevent="runSearch">
		<TextInput
			ref="searchInput"
			v-model="query"
			:placeholder="props.placeholder"
			class="mb-0 flex-1"
			:native-input-attrs="{ dir: 'auto', 'aria-label': props.placeholder }"
		/>
		<Dropdown placement="bottom-end">
			<template #trigger="{ show, hide, shown, triggerAttrs }">
				<Btn
					v-bind="triggerAttrs"
					type="button"
					thin
					flat
					icon="keyboard"
					title="Open Hebrew keyboard"
					aria-label="Open Hebrew keyboard"
					@click="shown ? hide() : show()"
				/>
			</template>
			<div class="hebrew-keyboard px-075 py-025" dir="ltr" aria-label="Hebrew keyboard">
				<div
					v-for="(row, rowIndex) in keyboardRows"
					:key="rowIndex"
					class="keyboard-row flex justify-content-center"
				>
					<Btn
						v-for="character in row"
						:key="character"
						type="button"
						thin
						:value="character"
						:aria-label="`Type ${character}`"
						@click="addCharacter(character)"
						class="keyboard-button"
					/>
				</div>
				<div class="keyboard-row flex justify-content-center">
					<Btn
						type="button"
						thin
						icon="backspace"
						aria-label="Delete last character"
						@click="removeCharacter"
						icon-size="0.9"
						class="keyboard-button keyboard-small"
					/>
					<Btn
						type="button"
						thin
						value="רווח"
						aria-label="Type a space"
						class="keyboard-button keyboard-space keyboard-small"
						@click="addCharacter(' ')"
					/>
					<Btn
						type="button"
						thin
						value="נקה"
						aria-label="Clear search"
						class="keyboard-button keyboard-small"
						@click="clearSearch"
					/>
				</div>
			</div>
		</Dropdown>
		<Btn type="submit" thin flat icon="search" aria-label="Search" />
	</form>
</template>

<style scoped>
.hebrew-search {
	background: var(--bgl-input-bg, white);
	border-radius: var(--bgl-input-border-radius);
}
.scrolledMenu .ShowOnScroll.hebrew-search {
	display: flex !important;
}

.hebrew-search :deep(.bagel-input input) {
	padding-inline-end: 0;
}

.hebrew-keyboard {
	width: min(23rem, calc(100vw - 2rem));
}

.keyboard-row {
	gap: 0.15rem;
	margin-bottom: 0.15rem;
}

.keyboard-row :deep(button) {
	min-width: 2.1rem;
	min-height: 2.1rem;
	font-size: 1.1rem;
}

.keyboard-button {
	padding-bottom: 0.15rem;
	--bgl-btn-border-radius: 0.5rem;
	background: var(--bgl-white);
	color: var(--bgl-black);
	border: 1px solid var(--bgl-black-20);
}

.keyboard-space {
	min-width: 6rem !important;
}
.keyboard-small {
	font-size: 0.9rem !important;
	padding-top: 0.1rem !important;
}
</style>
